import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from '@mui/material/styles';
import { GetActivityTypes, GetDashboardActivitiesGraphDatav2 } from 'services/BusinessIntelligence';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import { Alert, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { ArrowUpward, ArrowDownward, Edit, Delete } from '@mui/icons-material';
import { CirclePicker } from 'react-color';
import { NameIdObj } from 'common/models/GenericTypes';
import MultipleNumericValuesPicker from 'components/Pickers/MultipleNumericValuesPicker';
import { GetInterviewTypes } from 'services/InterviewsService';
import { GetActiveMeetingTypes, GetClientMeetingTypes } from 'services/MeetingsService';
import {
  ActivitiesGraphData,
  ActivityMetric,
  MetricFilterRequest,
} from 'common/models/Reporting/BusinessIntelligence';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import { InterviewType } from 'common/models/Configuration/InterviewType';

type CustomTooltipProps = TooltipProps<number, string>;

interface Props {
  id: string;
  title: string;
  sourceType: string;
  userFilterIds: number[];
  timeRange: number;
  kioskToken?: string | null;
  showConfigButton?: boolean;
  refreshFlag?: boolean | null;
  metrics?: string;
  saveConfigHandler?: (
    id: string,
    title: string,
    newTimeRange: number,
    sourceType: string,
    userFilterIds: number[],
    metrics: string
  ) => void;
  useDefaults: boolean;
}

const getDatesFromTimeRange = (tr: number) => {
  let start: moment.Moment | null = null;
  let end: moment.Moment | null = null;
  if (tr === 0) return;
  if (tr === 1) {
    // This Week
    start = moment().startOf('isoWeek');
    end = moment().endOf('isoWeek');
  } else if (tr === 2) {
    // This Month
    start = moment().startOf('month');
    end = moment().endOf('month');
  } else if (tr === 3) {
    // This Quarter
    start = moment().startOf('quarter');
    end = moment().endOf('quarter');
  } else if (tr === 4) {
    // This Year
    start = moment().startOf('year');
    end = moment().endOf('year');
  } else if (tr === 5) {
    // Last Week
    start = moment().subtract(1, 'week').startOf('isoWeek');
    end = moment().subtract(1, 'week').endOf('isoWeek');
  } else if (tr === 6) {
    // Last Month
    start = moment().subtract(1, 'months').startOf('month');
    end = moment().subtract(1, 'months').endOf('month');
  } else if (tr === 7) {
    // Last Quarter
    start = moment().subtract(1, 'quarter').startOf('quarter');
    end = moment().subtract(1, 'quarter').endOf('quarter');
  } else if (tr === 8) {
    // Last Year
    const lastYear = moment().get('year') - 1;
    start = moment(`${lastYear}-01-01`);
    end = moment(`${lastYear}-12-31`);
  } else if (tr === 9) {
    // Last 7 Days
    start = moment().subtract(6, 'days');
    end = moment();
  } else if (tr === 10) {
    // Last 30 Days
    start = moment().subtract(30, 'days');
    end = moment();
  } else if (tr === 11) {
    // Last 90 Days
    start = moment().subtract(90, 'days');
    end = moment();
  } else if (tr === 12) {
    // Last 365 Days
    start = moment().subtract(365, 'days');
    end = moment();
  }
  return [start, end];
};

export default function ActivitiesGraphv2Component({
  id,
  title: titleProp,
  sourceType: sourceTypeProps,
  userFilterIds: userIdsProps,
  timeRange: timeRangeProp,
  showConfigButton = false,
  kioskToken = null,
  refreshFlag = null,
  metrics: metricsProp,
  useDefaults,
  saveConfigHandler,
}: Props) {
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [timeRange, setTimeRange] = useState(1);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [openMetricsDialog, setOpenMetricsDialog] = useState(false);
  const [graphData, setGraphData] = useState<ActivitiesGraphData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [metricErrorMessage, setMetricErrorMessage] = useState('');
  const t = useTheme();

  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [metrics, setMetrics] = useState<ActivityMetric[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<ActivityMetric | null>(null);
  const [metricOptions, setMetricOptions] = useState<NameIdObj[]>([]);
  const [metricSpecialOptions, setMetricSpecialOptions] = useState<NameIdObj[]>([]);
  const [metricSpecialOptionLabel, setMetricSpecialOptionLabel] = useState<string>('');
  const [dateBasedOnOptions, setDateBasedOnOptions] = useState<NameIdObj[]>([]);
  const colors = [
    '#f44336',
    '#e91e63',
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#03a9f4',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#8bc34a',
    '#cddc39',
    '#ffeb3b',
    '#ffc107',
    '#ff9800',
    '#ff5722',
    '#795548',
    '#607d8b',
  ];

  const previousProps = useRef({
    showConfigDialog: showConfigDialog,
    sourceType: sourceType,
    timeRangeProp: timeRangeProp,
    metricsProp: metricsProp,
  });

  useEffect(() => {
    if (showConfigDialog) {
      setTitle(titleProp);
      setTimeRange(timeRangeProp);
    }
    if (userIdsProps) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(userIdsProps);
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(userIdsProps);
      }
    }
    setSourceType(sourceTypeProps);
  }, [showConfigDialog, timeRangeProp, titleProp, sourceTypeProps, userIdsProps]);

  function getRandomColor(): string {
    const randomIndex = Math.floor(Math.random() * colors.length);
    return colors[randomIndex];
  }
  const typeOptions = [
    { value: 1, label: 'Activity' },
    { value: 2, label: 'Interviews' },
    { value: 3, label: 'Jobs' },
    { value: 4, label: 'Meetings' },
    { value: 5, label: 'Placements' },
    { value: 6, label: 'Submissions' },
  ];

  const newMetric = {
    id: metrics.length + 1,
    name: `Metric ${metrics.length + 1}`,
    description: '',
    color: getRandomColor(),
    type: 0,
    subTypes: [],
    options: 0,
    dateBasedOn: 0,
    status: [1, 2],
  };

  useEffect(() => {
    const fetchData = async () => {
      let interviewTypes: InterviewType[] | null = [];
      let meetingTypes: NameIdObj[] | null = [];
      if (useDefaults) {
        try {
          interviewTypes = await GetInterviewTypes();
        } catch (error) {
          console.error('Error fetching interview types:', error);
        }

        try {
          meetingTypes = await GetClientMeetingTypes();
        } catch (error) {
          console.error('Error fetching meeting client types:', error);
        }
      }

      if (metricsProp) {
        const metrics = JSON.parse(metricsProp) as ActivityMetric[];
        const interviewsMetric = metrics.find((m) => m.type === 2 && m.subTypes.length === 0);
        const meetingsMetric = metrics.find((m) => m.type === 4 && m.subTypes.length === 0);

        if (interviewsMetric && interviewTypes) {
          interviewsMetric.subTypes = interviewTypes.map((t) => t.id);
        }
        if (meetingsMetric && meetingTypes) {
          meetingsMetric.subTypes = meetingTypes.map((t) => t.id);
        }

        setMetrics(metrics);
      }
    };

    fetchData();
  }, [metricsProp, useDefaults]);

  const handleChange = (color: any) => {
    setSelectedMetric((prev) => (prev ? { ...prev, color: color.hex } : null));
  };
  const getData = async () => {
    const dates = getDatesFromTimeRange(timeRangeProp);
    if (dates) {
      const [m1, m2] = dates;
      if (m1 && m2) {
        const start = m1.format('YYYY-MM-DD');
        const end = m2.format('YYYY-MM-DD');
        let filterTypeId = 0;

        if (sourceType == 'consultants') {
          filterTypeId = 4;
        } else if (sourceType == 'groups') {
          filterTypeId = 3;
        } else if (sourceType == 'currentuser') {
          filterTypeId = 5;
        } else {
          filterTypeId = 1;
        }

        setIsLoading(true);
        if (metricsProp) {
          const m = JSON.parse(metricsProp);
          const res = await GetDashboardActivitiesGraphDatav2(
            {
              startDate: start,
              endDate: end,
              filters: m,
              userFilterIds: mapIds(),
              userFilterTypeId: filterTypeId,
            } as MetricFilterRequest,
            kioskToken,
            setErrorMessage
          );

          if (res) {
            const enrichedGraphData = res.map((item) => {
              const matchingProp = m.find((prop: ActivityMetric) => prop.name === item.name);

              if (matchingProp && matchingProp.description) {
                return {
                  ...item,
                  description: `${matchingProp.description}`,
                };
              } else {
                return {
                  ...item,
                  description: '',
                };
              }
            });

            setGraphData(enrichedGraphData);
          }
        }
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      previousProps.current.showConfigDialog !== showConfigDialog ||
      previousProps.current.sourceType !== sourceType ||
      previousProps.current.timeRangeProp !== timeRangeProp ||
      previousProps.current.metricsProp !== metricsProp
    ) {
      !showConfigDialog && getData();

      previousProps.current = {
        showConfigDialog: showConfigDialog,
        sourceType: sourceType,
        timeRangeProp: timeRangeProp,
        metricsProp: metricsProp,
      };
    }
  }, [showConfigDialog, sourceType, timeRangeProp, metricsProp]);

  const validateMetric = (metric: ActivityMetric) => {
    if (!metric.name) {
      setMetricErrorMessage('Metric name is required');
      return false;
    }

    if (metric.type === 0) {
      setMetricErrorMessage('Metric type is required');
      return false;
    }

    if (metric.subTypes.length === 0) {
      setMetricErrorMessage(`${getLabelById(metric.type ?? 0)} is required.`);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (refreshFlag && kioskToken) {
      getData();
    }
  }, [refreshFlag, kioskToken]);

  const mapIds = useCallback(() => {
    let selectedIds: number[] = [];
    if (sourceType == 'consultants') {
      selectedIds = selectedUsers;
    } else if (sourceType == 'groups') {
      selectedIds = selectedGroups;
    }
    return selectedIds;
  }, [selectedUsers, selectedGroups, sourceType]);

  const onSaveConfig = useCallback(() => {
    let isValid = true;
    metrics.forEach((m) => {
      const valid = validateMetric(m);
      if (!valid) {
        setSelectedMetric(m);
        setOpenMetricsDialog(true);
        isValid = false;
        return;
      }
    });
    let selectedIds: number[] = [];
    if (sourceType == 'consultants') {
      selectedIds = selectedUsers;
    } else if (sourceType == 'groups') {
      selectedIds = selectedGroups;
    }
    if (isValid) {
      saveConfigHandler &&
        saveConfigHandler(id, title, timeRange, sourceType, selectedIds, JSON.stringify(metrics));
      setShowConfigDialog(false);
    }
  }, [id, saveConfigHandler, timeRange, title, sourceType, metrics, selectedUsers, selectedGroups]);

  const handleCancel = () => {
    setTitle(titleProp);
    setTimeRange(timeRangeProp);
    setSourceType(sourceTypeProps);
    setShowConfigDialog(false);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newItems = [...metrics];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= metrics.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    setMetrics(newItems);
  };

  const deleteItem = (id: number) => {
    setMetrics((prevMetrics) => {
      const newMetrics = prevMetrics.filter((metric) => metric.id !== id);
      return newMetrics.map((metric, index) => ({
        ...metric,
        id: index + 1, // Ensure IDs are sequential after deletion
      }));
    });
  };

  const editItem = useCallback(
    async (index: number) => {
      const metric = metrics[index];
      if (metric) {
        setSelectedMetric(metric);
        setOpenMetricsDialog(true);
        let res: NameIdObj[] | null = [];
        if (metric.type === 1) {
          res = await GetActivityTypes();
          setDateBasedOnOptions([
            { id: 0, name: 'Created Date' },
            { id: 1, name: 'Activity Date' },
          ] as NameIdObj[]);
        } else if (metric.type === 2) {
          res = await GetInterviewTypes();
          setDateBasedOnOptions([
            { id: 0, name: 'Created Date' },
            { id: 1, name: 'Interview Date' },
          ] as NameIdObj[]);
        } else if (metric.type === 3) {
          res = [
            { id: 2, name: 'Contract' },
            { id: 3, name: 'Fixed Term' },
            { id: 4, name: 'Maximum Term' },
            { id: 5, name: 'Margin Only' },
            { id: 1, name: 'Permanent' },
            { id: 6, name: 'Talent Pool' },
          ] as NameIdObj[];
          setMetricSpecialOptions([
            { id: 0, name: 'No' },
            { id: 1, name: 'Yes' },
          ] as NameIdObj[]);
          setMetricSpecialOptionLabel('Exclusive Only');
        } else if (metric.type === 4) {
          res = await GetActiveMeetingTypes(0);
          setDateBasedOnOptions([
            { id: 0, name: 'Created Date' },
            { id: 1, name: 'Meeting Date' },
          ] as NameIdObj[]);
        } else if (metric.type === 5) {
          res = [
            { id: 1, name: 'Permanent' },
            { id: 2, name: 'Contract' },
            { id: 3, name: 'Fixed Term Contract' },
            { id: 4, name: 'Maximum Term Contract' },
            { id: 5, name: 'Margin Only Contract' },
          ];
          setDateBasedOnOptions([
            { id: 0, name: 'Placement Date' },
            { id: 1, name: 'Start Date' },
            { id: 2, name: 'Created Date' },
          ] as NameIdObj[]);
          setMetricSpecialOptions([
            { id: 0, name: 'Yes' },
            { id: 1, name: 'No' },
          ] as NameIdObj[]);
          setMetricSpecialOptionLabel('New Placements only');
        } else if (metric.type === 6) {
          res = [
            { id: 1, name: 'Submission' },
            { id: 2, name: 'Float' },
          ] as NameIdObj[];
        }

        if (res) {
          setMetricOptions(res);
        }
      }
    },
    [
      setSelectedMetric,
      setOpenMetricsDialog,
      setMetricSpecialOptions,
      setDateBasedOnOptions,
      setMetricSpecialOptionLabel,
      metrics,
    ]
  );

  const addMetric = () => {
    const newId = metrics.length > 0 ? Math.max(...metrics.map((m) => m.id)) + 1 : 1;
    const metricToAdd = { ...newMetric, id: newId };
    setMetrics((prevMetrics) => [...prevMetrics, metricToAdd]);
    setSelectedMetric(metricToAdd);
    setOpenMetricsDialog(true);
  };

  const handleMetricClose = () => {
    setMetricErrorMessage('');
    setOpenMetricsDialog(false);
    setMetricOptions([]);
  };

  const handleTypeChange = useCallback(
    async ({ target }: React.ChangeEvent<{ value: unknown }>) => {
      const newType = Number(target.value);
      setSelectedMetric((prev) => (prev ? { ...prev, type: newType, subTypes: [] } : null));
      setMetricOptions([]);
      let res: NameIdObj[] | null = [];
      if (newType === 1) {
        res = await GetActivityTypes();
        setDateBasedOnOptions([
          { id: 0, name: 'Created Date' },
          { id: 1, name: 'Activity Date' },
        ] as NameIdObj[]);
      } else if (newType === 2) {
        res = await GetInterviewTypes();
        setDateBasedOnOptions([
          { id: 0, name: 'Created Date' },
          { id: 1, name: 'Interview Date' },
        ] as NameIdObj[]);
      } else if (newType === 3) {
        res = [
          { id: 2, name: 'Contract' },
          { id: 3, name: 'Fixed Term' },
          { id: 4, name: 'Maximum Term' },
          { id: 5, name: 'Margin Only' },
          { id: 1, name: 'Permanent' },
          { id: 6, name: 'Talent Pool' },
        ] as NameIdObj[];
        setMetricSpecialOptions([
          { id: 0, name: 'No' },
          { id: 1, name: 'Yes' },
        ] as NameIdObj[]);
        setMetricSpecialOptionLabel('Exclusive Only');
      } else if (newType === 4) {
        res = await GetActiveMeetingTypes(0);
        setDateBasedOnOptions([
          { id: 0, name: 'Created Date' },
          { id: 1, name: 'Meeting Date' },
        ] as NameIdObj[]);
      } else if (newType === 5) {
        res = [
          { id: 1, name: 'Permanent' },
          { id: 2, name: 'Contract' },
          { id: 3, name: 'Fixed Term Contract' },
          { id: 4, name: 'Maximum Term Contract' },
          { id: 5, name: 'Margin Only Contract' },
        ];
        setDateBasedOnOptions([
          { id: 0, name: 'Placement Date' },
          { id: 1, name: 'Start Date' },
          { id: 2, name: 'Created Date' },
        ] as NameIdObj[]);
        setMetricSpecialOptions([
          { id: 0, name: 'Yes' },
          { id: 1, name: 'No' },
        ] as NameIdObj[]);
        setMetricSpecialOptionLabel('New Placements only');
      } else if (newType === 6) {
        res = [
          { id: 1, name: 'Submission' },
          { id: 2, name: 'Float' },
        ] as NameIdObj[];
      }

      if (res) {
        setMetricOptions(res);
      }
    },
    [setSelectedMetric, setMetricOptions]
  );

  const onNumericValuesChange = useCallback(
    (values: NameIdObj[], property: string) => {
      const vals = values.map((v) => v.id);
      setSelectedMetric((prev) => (prev ? { ...prev, [property]: vals } : null));
    },
    [setSelectedMetric]
  );

  const onNumValuesChange = useCallback((values: NameIdObj[], type: string) => {
    const ids = values.map((v) => v.id);
    if (type == 'consultants') {
      setSelectedUsers(ids);
    }

    if (type == 'groups') {
      setSelectedGroups(ids);
    }
  }, []);

  const getLabelById = (id: number): string => {
    const item = typeOptions.find((option) => option.value === id);
    if (!item) return 'Sub Type';
    const singularLabel = item.label.endsWith('s') ? item.label.slice(0, -1) : item.label;
    return `${singularLabel} Type`;
  };

  const handleMetricSave = () => {
    if (selectedMetric === null) return;

    if (!selectedMetric.name) {
      setMetricErrorMessage('Metric name is required.');
      return;
    } else {
      setMetricErrorMessage('');
    }

    if (selectedMetric.type === 0) {
      setMetricErrorMessage('Metric type is required.');
      return;
    } else {
      setMetricErrorMessage('');
    }

    if (selectedMetric.subTypes.length === 0) {
      setMetricErrorMessage(`${getLabelById(selectedMetric?.type ?? 0)} is required.`);
      return;
    } else {
      setMetricErrorMessage('');
    }

    // Update the metrics state with the new/edited metric
    setMetrics((prevMetrics) => {
      // Check if the metric already exists in the array (update scenario)
      const metricIndex = prevMetrics.findIndex((metric) => metric.id === selectedMetric.id);

      if (metricIndex !== -1) {
        // If it exists, update the metric in the array
        const newMetrics = [...prevMetrics];
        newMetrics[metricIndex] = selectedMetric;
        return newMetrics;
      } else {
        // If it doesn't exist, add the new metric to the array
        return [...prevMetrics, selectedMetric];
      }
    });

    setOpenMetricsDialog(false);
  };

  const sourceTypeChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSourceType(value);
  }, []);

  function CustomTooltip({ active, payload }: CustomTooltipProps) {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ActivitiesGraphData; // Type assertion

      return (
        <div
          className="custom-tooltip"
          style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}
        >
          <p className="label" style={{ fontWeight: 'bold' }}>
            {data.name}
          </p>
          {data.description && <p className="desc">{data.description}</p>}
          <p className="value">Number: {data.value}</p>
        </div>
      );
    }

    return null;
  }

  return (
    <>
      <Dialog open={openMetricsDialog} fullWidth>
        <DialogTitle>Edit Metric</DialogTitle>
        <DialogContent>
          {metricErrorMessage && <Alert severity="error">{metricErrorMessage}</Alert>}
          <Stack spacing={2} mt="10px">
            <RWTextFieldComponent
              label="Name"
              value={selectedMetric?.name ?? ''}
              onChange={(e) =>
                setSelectedMetric((prev) => (prev ? { ...prev, name: e.target.value } : null))
              }
            />
            <RWTextFieldComponent
              label="Description"
              value={selectedMetric?.description ?? ''}
              multiline
              minRows={3}
              onChange={(e) =>
                setSelectedMetric((prev) =>
                  prev ? { ...prev, description: e.target.value } : null
                )
              }
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 4,
              }}
            >
              <div
                style={{
                  backgroundColor: selectedMetric?.color,
                  border: '1px solid #000',
                  display: 'inline-block',
                }}
              />

              <CirclePicker colors={colors} onChangeComplete={handleChange} />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button variant="contained" sx={{ background: selectedMetric?.color }}>
                  Selected Colour
                </Button>
              </Stack>
            </Box>
            <Stack spacing={2}>
              <TextField
                select
                label="Type"
                value={selectedMetric?.type}
                onChange={handleTypeChange}
                fullWidth
              >
                {typeOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              {metricOptions.length >= 1 && (
                <MultipleNumericValuesPicker
                  key={selectedMetric?.id}
                  label={getLabelById(selectedMetric?.type ?? 0)}
                  onSelect={(e) => {
                    onNumericValuesChange(e, 'subTypes');
                  }}
                  options={metricOptions}
                  value={selectedMetric?.subTypes ?? []}
                />
              )}
            </Stack>
            {(selectedMetric?.type === 3 || selectedMetric?.type === 5) && (
              <TextField
                select
                label={metricSpecialOptionLabel}
                value={selectedMetric?.options}
                onChange={(e) =>
                  setSelectedMetric((prev) => (prev ? { ...prev, options: +e.target.value } : null))
                }
                fullWidth
              >
                {metricSpecialOptions.map((d: NameIdObj) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {selectedMetric?.type === 1 && (
              <MultipleNumericValuesPicker
                key={selectedMetric?.id}
                label="Status"
                onSelect={(e) => onNumericValuesChange(e, 'status')}
                options={
                  [
                    { id: 1, name: 'Open' },
                    { id: 2, name: 'Closed' },
                  ] as NameIdObj[]
                }
                value={selectedMetric?.status ?? []}
              />
            )}
            {(selectedMetric?.type === 1 ||
              selectedMetric?.type === 2 ||
              selectedMetric?.type === 4 ||
              selectedMetric?.type === 5) && (
              <TextField
                select
                label="Date Based On"
                value={selectedMetric?.dateBasedOn}
                onChange={(e) =>
                  setSelectedMetric((prev) =>
                    prev ? { ...prev, dateBasedOn: +e.target.value } : null
                  )
                }
                fullWidth
              >
                {dateBasedOnOptions.map((d: NameIdObj) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={() => handleMetricClose()}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleMetricSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showConfigDialog} fullWidth>
        <DialogTitle>Configure Element</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt="10px">
            <RWTextFieldComponent
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              select
              label="Source Type"
              value={sourceType}
              onChange={sourceTypeChangeHandler}
              fullWidth
            >
              <MenuItem value="company">All</MenuItem>
              <MenuItem value="consultants">Consultant</MenuItem>
              <MenuItem value="currentuser">Current User</MenuItem>
              <MenuItem value="groups">Group</MenuItem>
            </TextField>
            {sourceType === 'consultants' && (
              <MultipleUserPicker
                value={selectedUsers}
                onSelect={(s) => {
                  onNumValuesChange(s, 'consultants');
                }}
                label="Consultants"
              />
            )}
            {sourceType === 'groups' && (
              <MultipleGroupPicker
                value={selectedGroups}
                onSelect={(s) => {
                  onNumValuesChange(s, 'groups');
                }}
                label="Groups"
              />
            )}
            <TextField
              select
              label="Time Range"
              value={timeRange.toString()}
              onChange={({ target }) => setTimeRange(+target.value)}
              fullWidth
            >
              <MenuItem value="1">This Week</MenuItem>
              <MenuItem value="2">This Month</MenuItem>
              <MenuItem value="3">This Quarter</MenuItem>
              <MenuItem value="4">This Year</MenuItem>
              <MenuItem value="5">Last Week</MenuItem>
              <MenuItem value="6">Last Month</MenuItem>
              <MenuItem value="7">Last Quarter</MenuItem>
              <MenuItem value="8">Last Year</MenuItem>
              <MenuItem value="9">Last 7 Days</MenuItem>
              <MenuItem value="10">Last 30 Days</MenuItem>
              <MenuItem value="11">Last 90 Days</MenuItem>
              <MenuItem value="12">Last 365 Days</MenuItem>
            </TextField>
            <Box>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" onClick={addMetric}>
                  Add Metric
                </Button>
              </Box>
              <List>
                {metrics.map((item, index) => (
                  <ListItem
                    sx={{ border: '1px solid #ccc', borderRadius: '4px', mb: 1 }}
                    key={item.id}
                    secondaryAction={
                      <>
                        <IconButton onClick={() => moveItem(index, 'up')} disabled={index === 0}>
                          <ArrowUpward />
                        </IconButton>
                        <IconButton
                          onClick={() => moveItem(index, 'down')}
                          disabled={index === metrics.length - 1}
                        >
                          <ArrowDownward />
                        </IconButton>
                        <IconButton onClick={() => editItem(index)}>
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => deleteItem(item.id)}>
                          <Delete />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={item.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={onSaveConfig} disabled={!titleProp}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showConfigButton && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 30,
            zIndex: (t) => t.zIndex.appBar - 5,
            color: (t) => t.palette.text.disabled,
          }}
          onClick={() => setShowConfigDialog(true)}
        >
          <SettingsIcon />
        </IconButton>
      )}
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" padding={1} textAlign="center" fontWeight={600} component="div">
          {titleProp}
        </Typography>
        {isLoading && (
          <Box m="auto">
            <CircularProgress />
          </Box>
        )}
        {errorMessage != '' && (
          <Typography
            variant="subtitle2"
            padding={1}
            textAlign="center"
            fontWeight={600}
            component="div"
          >
            {errorMessage}
          </Typography>
        )}
        {errorMessage == '' && (
          <div style={{ height: 'calc(100% - 48px)', display: isLoading ? 'none' : undefined }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart width={150} height={150} data={graphData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" type="category" stroke={t.palette.text.primary} />
                <YAxis type="number" dataKey="value" />
                <Tooltip
                  content={<CustomTooltip />}
                  contentStyle={{ backgroundColor: t.palette.background.paper }}
                />

                <Bar dataKey="value" name="Number" fill={t.palette.text.disabled}>
                  {graphData.map((d, i) => (
                    <Cell key={`cell-${i}`} fill={d.colour} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Paper>
    </>
  );
}
